import React, { useState } from 'react';
import { Check } from 'lucide-react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { backgroundLibrary, sectionTypes } from '../data/backgroundLibrary';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';

const BackgroundSelector = ({ sections, onSectionsChange }) => {
  const [selectedSection, setSelectedSection] = useState('global');
  const [selectedCategory, setSelectedCategory] = useState('solid');

  const currentSectionBg = sections[selectedSection] || {};
  
  const handleBackgroundSelect = (background) => {
    onSectionsChange({
      ...sections,
      [selectedSection]: background
    });
  };

  const getPreviewStyle = (bg) => {
    if (!bg) return {};
    
    switch (bg.type) {
      case 'solid':
        return { backgroundColor: bg.value };
      case 'gradient':
        return { background: bg.value };
      case 'pattern':
        return {
          backgroundColor: bg.color || '#F5F5F5',
          backgroundImage: bg.value,
          backgroundSize: bg.size || '20px 20px',
        };
      case 'image':
        return {
          backgroundImage: `url(${bg.thumbnail || bg.value})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        };
      default:
        return {};
    }
  };

  const categories = Object.entries(backgroundLibrary);

  return (
    <div className="space-y-4">
      {/* Section Selector */}
      <div>
        <Label className="text-sm font-medium mb-2 block">Bereich auswählen:</Label>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
          {sectionTypes.map((section) => (
            <Button
              key={section.id}
              variant={selectedSection === section.id ? 'default' : 'outline'}
              onClick={() => setSelectedSection(section.id)}
              className="text-sm h-auto py-3"
            >
              <div className="text-left">
                <div className="font-medium">{section.name}</div>
                <div className="text-xs opacity-70">{section.description}</div>
              </div>
            </Button>
          ))}
        </div>
      </div>

      {/* Current Selection Preview */}
      {currentSectionBg.name && (
        <div className="p-4 bg-gray-50 rounded-lg border">
          <Label className="text-xs text-gray-600 mb-2 block">Aktuell gewählt für {sectionTypes.find(s => s.id === selectedSection)?.name}:</Label>
          <div className="flex items-center gap-3">
            <div 
              className="w-16 h-16 rounded border-2 border-gray-300"
              style={getPreviewStyle(currentSectionBg)}
            />
            <div>
              <div className="font-medium text-sm">{currentSectionBg.name}</div>
              <div className="text-xs text-gray-500">
                {backgroundLibrary[selectedCategory]?.name || 'Hintergrund'}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Background Category Tabs */}
      <Tabs value={selectedCategory} onValueChange={setSelectedCategory}>
        <TabsList className="grid grid-cols-4 w-full">
          {categories.map(([key, cat]) => (
            <TabsTrigger key={key} value={key} className="text-xs">
              {cat.icon} {cat.name}
            </TabsTrigger>
          ))}
        </TabsList>

        {categories.map(([key, cat]) => (
          <TabsContent key={key} value={key} className="mt-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 max-h-96 overflow-y-auto p-2">
              {cat.items.map((bg) => {
                const isSelected = currentSectionBg.id === bg.id;
                
                return (
                  <button
                    key={bg.id}
                    onClick={() => handleBackgroundSelect(bg)}
                    className={`relative flex flex-col items-center p-3 rounded-lg border-2 transition-all hover:shadow-md ${
                      isSelected 
                        ? 'border-purple-500 ring-2 ring-purple-200 bg-purple-50' 
                        : 'border-gray-200 hover:border-purple-300'
                    }`}
                  >
                    {/* Background Preview */}
                    <div 
                      className="w-full h-20 rounded mb-2 border border-gray-300"
                      style={getPreviewStyle(bg)}
                    />
                    
                    {/* Name */}
                    <span className="text-xs font-medium text-center">{bg.name}</span>
                    
                    {/* Selected Check */}
                    {isSelected && (
                      <div className="absolute -top-2 -right-2 bg-purple-500 text-white rounded-full p-1">
                        <Check className="h-3 w-3" />
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          </TabsContent>
        ))}
      </Tabs>

      {/* Info */}
      <div className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-3">
        <strong>💡 Tipp:</strong> Wählen Sie unterschiedliche Hintergründe für verschiedene Bereiche, 
        um Ihrer Website mehr Tiefe und visuelle Abwechslung zu verleihen.
      </div>
    </div>
  );
};

export default BackgroundSelector;
